import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PublicHeader from './shared/PublicHeader';
import PublicFooter from './shared/PublicFooter';
import './PublicJobListings.css';

const PublicJobListings = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ experienceLevel: '', workMode: '', employmentType: '' });
  const [showTalentForm, setShowTalentForm] = useState(false);
  const [talentForm, setTalentForm] = useState({ name: '', email: '', phone: '', area: '', resume: null, message: '' });
  const [submittingTalent, setSubmittingTalent] = useState(false);
  const [talentSubmitted, setTalentSubmitted] = useState(false);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/jobs/public');
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load jobs');
      setJobs(data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const term = searchTerm.toLowerCase().trim();
    if (term) {
      const matches = (job.title || '').toLowerCase().includes(term)
        || (job.location || '').toLowerCase().includes(term)
        || (job.department || '').toLowerCase().includes(term)
        || (job.description || '').toLowerCase().includes(term)
        || (job.skills || []).some(s => s.toLowerCase().includes(term));
      if (!matches) return false;
    }
    if (filters.experienceLevel && job.experienceLevel !== filters.experienceLevel) return false;
    if (filters.workMode && job.workMode !== filters.workMode) return false;
    if (filters.employmentType && job.employmentType !== filters.employmentType) return false;
    return true;
  });

  const hasActiveFilters = searchTerm || filters.experienceLevel || filters.workMode || filters.employmentType;

  const clearAll = () => {
    setSearchTerm('');
    setFilters({ experienceLevel: '', workMode: '', employmentType: '' });
  };

  const handleJobClick = (job) => {
    navigate(`/jobs/${job.slug || job._id}`);
  };

  const handleTalentSubmit = async (e) => {
    e.preventDefault();
    if (!talentForm.name || !talentForm.email || !talentForm.phone || !talentForm.area) return;
    setSubmittingTalent(true);
    try {
      const formData = new FormData();
      formData.append('name', talentForm.name);
      formData.append('email', talentForm.email);
      formData.append('phone', talentForm.phone);
      formData.append('areaOfInterest', talentForm.area);
      formData.append('message', talentForm.message);
      if (talentForm.resume) formData.append('resume', talentForm.resume);
      await fetch('/api/jobs/talent-pool', { method: 'POST', body: formData });
      setTalentSubmitted(true);
    } catch (err) {
      console.error('Talent pool submission failed:', err);
    } finally {
      setSubmittingTalent(false);
    }
  };

  if (loading) {
    return (
      <>
        <PublicHeader jobCount={jobs.length} />
        <div className="jobs-listing-container">
          <div className="loading-state"><div className="spinner"></div><p>Loading available positions...</p></div>
        </div>
        <PublicFooter />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Open Positions - SecureLayer7 Careers | Cybersecurity Jobs</title>
        <meta name="description" content="Browse open cybersecurity positions at SecureLayer7. Roles in penetration testing, red team operations, security consulting, cloud security, and more. Austin, TX & Pune, India." />
        <link rel="canonical" href="https://sechire.net/jobs" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sechire.net/jobs" />
        <meta property="og:title" content="Open Positions - SecureLayer7 Careers" />
        <meta property="og:description" content="Browse open cybersecurity positions at SecureLayer7. Roles in penetration testing, red team operations, security consulting, and more." />
        <meta property="og:image" content="https://securelayer7.net/images/logo.png" />
        <meta property="og:site_name" content="SecureLayer7 Careers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://sechire.net/jobs" />
        <meta name="twitter:title" content="Open Positions - SecureLayer7 Careers" />
        <meta name="twitter:description" content="Browse open cybersecurity positions at SecureLayer7. Penetration testing, red team, security consulting, and more." />
        <meta name="twitter:image" content="https://securelayer7.net/images/logo.png" />
        {filteredJobs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: 'SecureLayer7 Open Positions',
              description: 'Current cybersecurity job openings at SecureLayer7',
              numberOfItems: filteredJobs.length,
              itemListElement: filteredJobs.map((job, idx) => ({
                '@type': 'ListItem',
                position: idx + 1,
                name: job.title,
                url: `https://sechire.net/jobs/${job.slug || job._id}`
              }))
            })}
          </script>
        )}
      </Helmet>

      <PublicHeader jobCount={jobs.length} />

      <main className="jobs-listing-container">
        <header className="jobs-hero">
          <div className="hero-content">
            <h1>Open Positions</h1>
            <p className="hero-subtitle">
              Work on cutting-edge security challenges. Build your career in cybersecurity.
            </p>
          </div>
        </header>

        {/* Search & Filters */}
        <div className="jobs-search-section">
          <div className="search-input-container">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by role, skill, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="search-clear-btn" aria-label="Clear">&times;</button>
            )}
          </div>
          <div className="filters-row">
            <select value={filters.experienceLevel} onChange={(e) => setFilters(f => ({ ...f, experienceLevel: e.target.value }))} className="filter-select">
              <option value="">All Levels</option>
              <option value="entry">Entry</option>
              <option value="junior">Junior</option>
              <option value="mid">Mid Level</option>
              <option value="senior">Senior</option>
              <option value="lead">Lead</option>
              <option value="principal">Principal</option>
            </select>
            <select value={filters.workMode} onChange={(e) => setFilters(f => ({ ...f, workMode: e.target.value }))} className="filter-select">
              <option value="">All Work Modes</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
              <option value="onsite">On-site</option>
            </select>
            <select value={filters.employmentType} onChange={(e) => setFilters(f => ({ ...f, employmentType: e.target.value }))} className="filter-select">
              <option value="">All Types</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="contract">Contract</option>
              <option value="internship">Internship</option>
            </select>
            {hasActiveFilters && (
              <button onClick={clearAll} className="clear-filters">Clear</button>
            )}
          </div>
          {hasActiveFilters && (
            <p className="results-count">{filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'} found</p>
          )}
        </div>

        <div className="jobs-content">
          {error && (
            <div className="error-message"><p>Failed to load job listings. Please try again later.</p></div>
          )}

          {!error && filteredJobs.length === 0 && !loading && (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <h2 className="empty-state-title">{hasActiveFilters ? 'No Matching Positions' : 'No Positions Available'}</h2>
              <p>{hasActiveFilters
                ? "No positions match your search. Try adjusting your filters."
                : "We don't have any open positions at the moment."}</p>
              {hasActiveFilters && <button onClick={clearAll} className="clear-filters" style={{ marginTop: '1rem' }}>Clear filters</button>}
            </div>
          )}

          {filteredJobs.length > 0 && (
            <div className="jobs-grid">
              {filteredJobs.map((job) => (
                <article key={job._id} className="job-card" onClick={() => handleJobClick(job)}>
                  <div className="job-card-header">
                    <h3 className="job-title">{job.title}</h3>
                    <div className="job-company">{job.company || 'SecureLayer7'}</div>
                  </div>
                  <div className="job-details">
                    <div className="job-detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      <span>{job.location}</span>
                    </div>
                    <div className="job-detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                      <span className="capitalize">{job.workMode}</span>
                    </div>
                    <div className="job-detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      <span className="capitalize">{job.employmentType?.replace('-', ' ')}</span>
                    </div>
                    <div className="job-detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                      <span className="capitalize">{job.experienceLevel} Level</span>
                    </div>
                  </div>
                  {job.description && (
                    <p className="job-description">{job.description.substring(0, 150)}{job.description.length > 150 && '...'}</p>
                  )}
                  <div className="job-card-footer">
                    <button className="view-job-btn">
                      View Details
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Talent Pool */}
        <div className="talent-pool-wrapper">
          {!showTalentForm && !talentSubmitted && (
            <div className="talent-cta">
              <p>Can't find a role that fits?</p>
              <button onClick={() => setShowTalentForm(true)} className="talent-btn">Join our Talent Pool</button>
              <span className="talent-hint">We'll reach out when a matching role opens.</span>
            </div>
          )}
          {talentSubmitted && (
            <div className="talent-success">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <p>You're on our radar! We'll notify you when a matching position opens.</p>
            </div>
          )}
          {showTalentForm && !talentSubmitted && (
            <form className="talent-form" onSubmit={handleTalentSubmit}>
              <h3>Join Our Talent Pool</h3>
              <p className="talent-form-subtitle">Tell us what you're looking for and we'll reach out when there's a match.</p>
              <div className="talent-grid">
                <input type="text" placeholder="Full Name *" required value={talentForm.name} onChange={e => setTalentForm(f => ({ ...f, name: e.target.value }))} />
                <input type="email" placeholder="Email *" required value={talentForm.email} onChange={e => setTalentForm(f => ({ ...f, email: e.target.value }))} />
                <input type="tel" placeholder="Phone *" required value={talentForm.phone} onChange={e => setTalentForm(f => ({ ...f, phone: e.target.value }))} />
                <select required value={talentForm.area} onChange={e => setTalentForm(f => ({ ...f, area: e.target.value }))}>
                  <option value="">Area of Interest *</option>
                  <option value="penetration-testing">Penetration Testing</option>
                  <option value="red-team">Red Team Operations</option>
                  <option value="security-consulting">Security Consulting</option>
                  <option value="source-code-review">Source Code Review</option>
                  <option value="cloud-security">Cloud Security</option>
                  <option value="appsec">Application Security</option>
                  <option value="devsecops">DevSecOps</option>
                  <option value="project-management">Project Management</option>
                  <option value="sales-marketing">Sales & Marketing</option>
                  <option value="engineering">Software Engineering</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea placeholder="Anything else you'd like us to know? (experience, certifications, preferred location, etc.)" value={talentForm.message} onChange={e => setTalentForm(f => ({ ...f, message: e.target.value }))} rows="3" />
              <label className="talent-resume-upload">
                <span>{talentForm.resume ? talentForm.resume.name : 'Attach Resume (optional)'}</span>
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setTalentForm(f => ({ ...f, resume: e.target.files[0] || null }))} style={{ display: 'none' }} />
              </label>
              <div className="talent-actions">
                <button type="button" onClick={() => setShowTalentForm(false)} className="talent-cancel">Cancel</button>
                <button type="submit" disabled={submittingTalent} className="talent-submit">{submittingTalent ? 'Submitting...' : 'Submit'}</button>
              </div>
            </form>
          )}
        </div>
      </main>

      <PublicFooter />
    </>
  );
};

export default PublicJobListings;
